import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams, matchPath } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { createConsumer, getConfig } from '@rails/actioncable';
import last from 'lodash/last';
import moment from 'moment-timezone';
import IdleTimer from 'react-idle-timer';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';
import WorkspaceMeetingModeSchema from 'Shared/utils/normalizeJSON/schemas/WorkspaceMeetingMode.schema';
import FeedbackSchema from 'Shared/utils/normalizeJSON/schemas/Feedback.schema';
import QueryParamsConstants from 'Shared/constants/QueryParams.constants';
import { ESSENTIALS_TOOLTIP } from 'Shared/constants/PerformEssentials.constants';

// Types
import { TemplateWizardStateType } from 'Shared/types/Workspace.type';
import { Feature } from 'Shared/types/Organization.type';

// Helpers
import { toggleIntercom } from 'Shared/helpers';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useApp } from 'Shared/utils/hooks/use-app';
import { useToggle, useWorkspaceHealthCheck, useDeepEqualSelector, useFeature } from 'Shared/utils/hooks';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useBaTracking } from 'Shared/tracking/use-ba-tracking';
import { useSmac } from 'Shared/utils/hooks/use-smac';
import { useTooltip } from 'Wpds/components/tooltip/useTooltip';

// API
import { updatePresenceData } from 'Shared/api';

// Contexts
import WorkspaceActionCableContext from 'Shared/contexts/WorkspaceActionCableContext';

// Selectors
import { getWorkspaceTeammate } from 'Shared/selectors/workspace';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';
import normalizer from 'Shared/utils/normalizeJSON/normalizer';

// Actions
import {
  getWorkspace,
  clearWorkspace,
  partialResetWorkspaceNotifications,
  resetWorkspaceNotifications,
} from 'Shared/actions/workspaces';
import { clearWorkspacePrivateNoteAction } from 'Shared/actions/workspacePrivateNote';
import * as WorkspaceMeetingModeActions from 'Shared/actions/WorkspaceMeetingMode.actions';
import { feedbacksSyncSuccess, fetchFeedbackDrafts } from 'Shared/actions/Feedbacks.actions';
import { fetchAdditionalItems } from 'Shared/actions/todos';

// Components
import { WpPageLayout, WpPageHeader, WpPageBody, WpPageSideRail } from 'Wpds/components/page-layout';
import { WpTabs, WpTab } from 'Wpds/components/tabs';
import WpBackdrop from 'Wpds/components/backdrop/WpBackdrop';
import { Portal } from 'Wpds/components/portal/Portal';
import WorkspacePageTitle from './partials/WorkspacePageTitle';
import WorkspaceDetailsModal from 'Shared/components/WorkspaceDetailsModal/WorkspaceDetailsModal';
import WorkspaceLoading from './partials/WorkspaceLoading';
import WorkspaceHomeTab from './pages/WorkspaceHomeTab/WorkspaceHomeTab';
import WorkspaceGoalsTab from './pages/WorkspaceGoalsTab/WorkspaceGoalsTab';
import WorkspacePrivateTab from './pages/WorkspacePrivateNotesTab/WorkspacePrivateTab';
import WorkspaceFeedbackTab from './pages/WorkspaceFeedbackTab/WorkspaceFeedbackTab';
import WorkspaceHistoryTab from './pages/WorkspaceHistoryTab/WorkspaceHistoryTab';
import FeedbackCreationPage from 'Dashboard/components/FeedbackCreationPage/FeedbackCreationPage';
import WorkspaceMeetModal from 'Dashboard/components/MeetingMode/WorkspaceMeetModal';
import WorkspaceSidebar from './partials/WorkspaceSidebar/WorkspaceSidebar';
import GiveRecognitionModal from 'Shared/components/modals/GiveRecognitionModal/GiveRecognitionModal';
import WorkspaceIntroModal from 'Dashboard/components/WorkspaceIntroMoal/WorkspaceIntroModal';
import JoinMeetingConferenceModal from 'Shared/components/MeetingConference/JoinMeetingConferenceModal/JoinMeetingConferenceModal';
import AgendaBuilder from './pages/AgendaBuilder/AgendaBuilder';
import WorkspaceRelationshipTab from './pages/WorkspaceRelationshipTab/WorkspaceRelationshipTab';
import { WpIcon } from 'Wpds/components/icon';
import WorkspaceSideRail from './partials/WorkspaceSideRail/WorkspaceSideRail';
import WpTooltipOverlay from 'Wpds/components/tooltip/WpTooltipOverlay';

// Styles
import './WorkspacePage.scss';
import './WorkspacePagePrint.scss';

enum Tabs {
  workspace = 'workspace',
  goals = 'goals',
  private = 'private',
  feedback = 'feedback',
  history = 'history',
  relationship = 'relationship',
}

const oneMinute = 1000 * 60;
const cable = createConsumer(getConfig('url'));
let meetingItemChannel = null;

const WorkspacePage = (): JSX.Element => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const bodyRef = useRef(null);

  const tooltip = useTooltip();

  const { isFreePlan: isGoalsFreePlan, isFreeTrial: isGoalsFreeTrial } = useFeature(Feature.goals);
  const { isPartnerAccount } = useSmac();

  const { trackRelationshipTabButtonClick } = useBaTracking();
  const { app, isZoom } = useApp();

  const {
    newFeatureIntroModals: showNewFeatureModal,
    workspaceHistorySnapshotsUi: showAgendaHistory,
    newWorkspaceAgendaBuilder,
    featureFlagShowRelationshipGuideInWorkspace,
    enableDesignInWorkspaces,
  } = useFlags();
  const { workspaceId, feedbackRecognitionRequestId } = useParams();
  const { slug: currentUserSlug, isPiUser } = useCurrentUser();
  const {
    isOneOnOne,
    id: workspaceUuid,
    isPersonal,
    isGroup,
    loading: isLoading,
    workspace,
    otherUserInOneOnOne,
    users,
  } = useWorkspace();
  const { isPiOrganization, isPiDemoAccount } = useOrganization();

  const startMeetingPath = matchPath(location.pathname, { path: PageUrls.WORKSPACE_START_MEETING });
  const recurringMeetingConferenceUrl = useSelector(state => state.workspace.recurringMeeting?.conferenceUrl);
  const { isActive: isKudosActive } = useFeature(Feature.kudos);

  const [isSideRailOpen, toggleIsSideRailOpen] = useToggle(false);
  const [isKudosModalOpen, toggleIsKudosModalOpen] = useToggle(false);
  const [isSidebarOpen, toggleIsSidebarOpen] = useToggle(false);
  const [isWorkspaceDetailsModalOpen, toggleIsWorkspaceDetailsModalOpen] = useToggle(false);
  const [locationToSwitch, setLocationToSwitch] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [lostSocketConnectionAt, setLostSocketConnectionAt] = useState(null);

  const teammate = useDeepEqualSelector(getWorkspaceTeammate(workspaceId));

  const showJoinMeetingConferenceModal = recurringMeetingConferenceUrl && startMeetingPath;

  const emptyRecognitionRequest = {
    workspaceUuid: workspaceId,
    id: feedbackRecognitionRequestId,
    employee: teammate,
  };

  const handleCloseWorkspaceDetailsModal = useCallback(() => {
    toggleIsWorkspaceDetailsModalOpen();

    history.push(locationToSwitch);
  }, [workspaceId, locationToSwitch]);

  const handleKudosModalClose = () => {
    if (location.pathname.includes(QueryParamsConstants.RECOGNITION_REQUEST)) {
      history.push(PageUrlsUtil.makeWorkspaceFeedbackUrl(workspaceId));
    }
    toggleIsKudosModalOpen();
  };

  const redirectToWorkspace = useCallback(() => history.push(PageUrlsUtil.makeWorkspaceUrl(workspaceId)), [
    history,
    workspaceId,
  ]);

  useEffect(() => {
    dispatch(getWorkspace(workspaceId)).then(() => {
      dispatch(partialResetWorkspaceNotifications(workspaceId));
    });

    return () => {
      dispatch(clearWorkspace());
      dispatch({ type: 'CLEAR_FEEDBACKS' });
      dispatch(clearWorkspacePrivateNoteAction());
    };
  }, [workspaceId]);

  useEffect(() => {
    const isModalPath = !!matchPath(location.pathname, { path: PageUrls.WORKSPACE_SETTINGS });

    if (isModalPath) {
      toggleIsWorkspaceDetailsModalOpen();
    }
  }, [location]);

  useEffect(() => {
    if (location?.state?.isModal && Boolean(location?.state?.previousLocation)) {
      setLocationToSwitch(location?.state?.previousLocation);
    } else if (matchPath(location.pathname, { path: PageUrls.WORKSPACE_SETTINGS }) && !location?.state?.isModal) {
      setLocationToSwitch({ pathname: PageUrlsUtil.makeWorkspaceUrl(workspaceId) });
    } else {
      setLocationToSwitch(location);
    }
  }, [location]);

  useEffect(() => {
    if (matchPath(locationToSwitch?.pathname, { path: PageUrls.WORKSPACE_GOALS_PAGE })) {
      setActiveTab(Tabs.goals);
    } else if (matchPath(locationToSwitch?.pathname, { path: PageUrls.WORKSPACE_PRIVATE_PAGE })) {
      setActiveTab(Tabs.private);
    } else if (matchPath(locationToSwitch?.pathname, { path: PageUrls.WORKSPACE_HISTORY_PAGE })) {
      setActiveTab(Tabs.history);
    } else if (
      matchPath(locationToSwitch?.pathname, {
        path: ['/workspaces/:workspaceId/feedback', '/workspaces/:workspaceId/recognition_request'],
      })
    ) {
      setActiveTab(Tabs.feedback);
    } else if (matchPath(locationToSwitch?.pathname, { path: PageUrls.WORKSPACE_PAGE })) {
      setActiveTab(Tabs.workspace);
    } else if (matchPath(locationToSwitch?.pathname, { path: PageUrls.WORKSPACE_RELATIONSHIP_PAGE })) {
      setActiveTab(Tabs.relationship);
    }
  }, [locationToSwitch]);

  useEffect(() => {
    if (location.pathname.includes(QueryParamsConstants.RECOGNITION_REQUEST) && !isKudosModalOpen)
      toggleIsKudosModalOpen();
  }, [isKudosModalOpen, toggleIsKudosModalOpen, location]);

  const setRemoteHealthCheckData = useWorkspaceHealthCheck({ workspaceId });

  const createOrUpdateChannels = useCallback(() => {
    if (!workspaceId) return;

    if (meetingItemChannel) {
      const id = JSON.parse(meetingItemChannel.identifier).workspace_id;
      if (id === workspaceId) return;

      meetingItemChannel.unsubscribe();
      meetingItemChannel = null;
    }

    meetingItemChannel = cable.subscriptions.create(
      { channel: 'MeetingChannel', workspace_id: workspaceId },
      {
        received: json => {
          if (json.presenceData) {
            dispatch({
              type: 'PRESENCE_DATA_SYNC',
              payload: json.presenceData,
            });
          }

          if (json.healthCheck) {
            setRemoteHealthCheckData(json.healthCheck);
          }

          if (json.meetingModeSession) {
            dispatch(
              WorkspaceMeetingModeActions.actionCableUpdateMeetingModeSuccess(
                normalizer(WorkspaceMeetingModeSchema, json.meetingModeSession)
              )
            );
          }

          // Don't sync if the current client created the change that caused a sync
          if (json.clientSyncToken === app.clientSyncToken || !json.workspace_items) return;
          if (json.workspace_items.feedbacks)
            dispatch(feedbacksSyncSuccess(normalizer(FeedbackSchema, json.workspace_items.feedbacks)));
          dispatch({
            type: 'MEETING_ITEMS_SYNC',
            meetingItems: json.workspace_items.meeting_items,
            discussionTopics: json.workspace_items.discussion_topics,
            deletedMeetingItems: json.workspace_items.deleted_meeting_items,
            deletedDiscussionTopics: json.workspace_items.deleted_discussion_topics,
          });
        },
        connected: () => {
          // re-sync meeting items if we regained a lost connection more than 60 seconds ago
          if (lostSocketConnectionAt && moment().unix() - lostSocketConnectionAt > 60)
            dispatch(fetchAdditionalItems({ workspaceId }));
          setLostSocketConnectionAt(null);
        },
        disconnected: () => {
          setLostSocketConnectionAt(moment().unix());
        },
      }
    );
  }, [workspaceId, app.clientSyncToken, dispatch, lostSocketConnectionAt, setRemoteHealthCheckData]);

  useEffect(() => {
    createOrUpdateChannels();
    if (workspaceId) {
      dispatch(fetchFeedbackDrafts({ workspaceSlug: workspaceId }));
    }

    return () => {
      if (meetingItemChannel) {
        meetingItemChannel.unsubscribe();
        meetingItemChannel = null;
      }
      if (workspaceId) dispatch(resetWorkspaceNotifications(workspaceId));
    };
  }, [workspaceId]);

  const updateUserActive = useCallback(
    ({ active }) =>
      updatePresenceData({
        workspaceId,
        workspaceItemUuid: dispatch((_, getState) => last(getState().selectedUuids)),
        active,
      }),
    [workspaceId, dispatch]
  );

  const showDesignSideRail = isGroup && users.length >= 3 && users.length <= 25 && enableDesignInWorkspaces;

  const handleUserActive = useCallback(() => updateUserActive({ active: true }), [updateUserActive]);

  const handleUserIdle = useCallback(() => updateUserActive({ active: false }), [updateUserActive]);

  useEffect(() => {
    toggleIntercom({ show: !isSidebarOpen || !isSideRailOpen });

    return function cleanup() {
      toggleIntercom({ show: true });
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    toggleIsSideRailOpen(showDesignSideRail);
  }, [showDesignSideRail, toggleIsSideRailOpen]);

  const baseClassName = 'WorkspacePage';

  const showAgendaBuilder =
    newWorkspaceAgendaBuilder &&
    ((!workspace.initialTemplateSelectionMade &&
      workspace?.isOneOnOne &&
      workspace.discussion_topics.length === 0 &&
      !workspace.templateWizardState) ||
      (workspace.templateWizardState === TemplateWizardStateType.INITIALIZED &&
        workspace.templateWizardInitiatorSlug === currentUserSlug));

  const showRelationshipTab =
    featureFlagShowRelationshipGuideInWorkspace &&
    !isZoom &&
    isOneOnOne &&
    (isPiDemoAccount || (isPiOrganization && isPiUser && otherUserInOneOnOne?.piUserGuid));

  const getTabTitle = () => {
    if (isGroup) {
      return 'Kudos';
    }
    if (isKudosActive) {
      return 'Feedback & Kudos';
    }
    return 'Feedback';
  };

  if (!workspaceId || isLoading || workspaceId !== workspaceUuid) return <WorkspaceLoading />;

  return (
    <WorkspaceActionCableContext.Provider value={meetingItemChannel}>
      <IdleTimer onActive={handleUserActive} onIdle={handleUserIdle} timeout={oneMinute} />
      {showAgendaBuilder ? (
        <WpPageBody>
          <AgendaBuilder workspace={workspace} />
        </WpPageBody>
      ) : (
        <WpPageLayout
          width="full"
          className={baseClassName}
          pageRef={bodyRef}
          isSideRailOpen={isSideRailOpen}
          toggleIsSideRailOpen={toggleIsSideRailOpen}
        >
          <WpPageHeader sticky>
            <WorkspacePageTitle
              workspaceId={workspaceId}
              toggleSidebar={toggleIsSidebarOpen}
              isSideRailOpen={isSideRailOpen}
              toggleIsSideRailOpen={toggleIsSideRailOpen}
            />
            <WpTabs activeTab={activeTab}>
              <WpTab
                to={PageUrlsUtil.makeWorkspaceUrl(workspaceId)}
                exact
                tabId={Tabs.workspace}
                qaTest="workspace-workspace-page-tab"
              >
                Workspace
              </WpTab>
              {showRelationshipTab && (
                <WpTab
                  to={PageUrlsUtil.makeWorkspaceRelationshipUrl(workspaceId)}
                  exact
                  tabId={Tabs.relationship}
                  qaTest="workspace-relationship-page-tab"
                  trackOnClick={trackRelationshipTabButtonClick}
                >
                  Relationship
                </WpTab>
              )}
              {!isPersonal && (isKudosActive || !isGroup) && (
                <WpTab
                  to={PageUrlsUtil.makeWorkspaceFeedbackUrl(workspaceId)}
                  exact
                  tabId={Tabs.feedback}
                  qaTest="workspace-kudos-page-tab"
                >
                  {getTabTitle()}
                </WpTab>
              )}
              <WpTab
                to={PageUrlsUtil.makeWorkspaceGoalsUrl(workspaceId)}
                exact
                tabId={Tabs.goals}
                qaTest="workspace-goals-page-tab"
                ref={tooltip.targetRef}
              >
                Goals
                {(isGoalsFreePlan || isGoalsFreeTrial || isPiDemoAccount || isPartnerAccount) && (
                  <span className={`${baseClassName}__tabs__tab__locked-icon`}>
                    <WpIcon name="lock-alt" />
                  </span>
                )}
              </WpTab>
              {!isPersonal && (
                <WpTab
                  to={PageUrlsUtil.makeWorkspacePrivateUrl(workspaceId)}
                  exact
                  tabId={Tabs.private}
                  qaTest="workspace-private-page-tab"
                >
                  Private Notes
                </WpTab>
              )}
              <WpTab
                to={PageUrlsUtil.makeWorkspaceHistoryUrl(workspaceId)}
                tabId={Tabs.history}
                qaTest="workspace-history-page-tab"
              >
                {showAgendaHistory ? 'History' : 'Completed Items'}
              </WpTab>
            </WpTabs>
          </WpPageHeader>
          <WpPageBody>
            <Switch location={locationToSwitch}>
              <Route
                exact
                path={[
                  PageUrls.WORKSPACE_START_MEETING,
                  PageUrls.WORKSPACE_MEETING_ITEM_COMMENTS_COMMENT_UUID,
                  PageUrls.WORKSPACE_MEETING_ITEM,
                  PageUrls.WORKSPACE_PAGE,
                ]}
                component={WorkspaceHomeTab}
              />
              <Route exact path={PageUrls.WORKSPACE_RELATIONSHIP_PAGE} component={WorkspaceRelationshipTab} />
              <Route
                exact
                path={[PageUrls.WORKSPACE_GOALS_PAGE, PageUrls.WORKSPACE_GOALS_DETAILS_PANEL]}
                component={WorkspaceGoalsTab}
              />
              <Route exact path={PageUrls.WORKSPACE_PRIVATE_PAGE} component={WorkspacePrivateTab} />
              <Route
                exact
                path={[
                  PageUrls.WORKSPACE_HISTORY_PAGE,
                  PageUrls.WORKSPACE_HISTORY_PAGE_ITEMS,
                  PageUrls.WORKSPACE_HISTORY_PAGE_ITEMS_ITEM_COMMENTS_PAGE,
                  PageUrls.WORKSPACE_AGENDA_HISTORY,
                  PageUrls.WORKSPACE_AGENDA_HISTORY_ITEM_COMMENTS,
                ]}
                component={WorkspaceHistoryTab}
              />
              <Route
                exact
                path={[
                  '/workspaces/:workspaceId/feedback/:feedbackUuid',
                  '/workspaces/:workspaceId/feedback',
                  '/workspaces/:workspaceId/recognition_request/new',
                  '/workspaces/:workspaceId/recognition_request/:feedbackRecognitionRequestId',
                ]}
                render={() => <WorkspaceFeedbackTab pageRef={bodyRef} />}
              />
              <Route
                path={[
                  PageUrls.WORKSPACE_EDIT_FEEDBACK_FORM_PAGE,
                  PageUrls.WORKSPACE_FEEDBACK_FORM_PAGE,
                  PageUrls.WORKSPACE_FEEDBACK_REQUEST_PAGE,
                ]}
                component={FeedbackCreationPage}
              />
              <Route
                path={[PageUrls.WORKSPACE_MEETING_MODE, PageUrls.WORKSPACE_MEETING_MODE_ITEM]}
                render={() => <WorkspaceMeetModal />}
              />
            </Switch>
          </WpPageBody>
          {showDesignSideRail && (
            <WpPageSideRail title="Team Info">
              <WorkspaceSideRail />
            </WpPageSideRail>
          )}
        </WpPageLayout>
      )}
      <Portal idPrefix="workspace-sidebar-portal">
        <div className={`${baseClassName}__sidebar`}>
          <CSSTransition
            in={isSidebarOpen}
            timeout={350}
            classNames={`${baseClassName}__sidebar__animation`}
            unmountOnExit
          >
            <div className={`${baseClassName}__sidebar__content`}>
              <WorkspaceSidebar toggleSidebar={toggleIsSidebarOpen} />
            </div>
          </CSSTransition>
          {isSidebarOpen && <WpBackdrop onClick={toggleIsSidebarOpen} />}
        </div>
      </Portal>
      {isWorkspaceDetailsModalOpen && (
        <WorkspaceDetailsModal isOpen={isWorkspaceDetailsModalOpen} toggleIsOpen={handleCloseWorkspaceDetailsModal} />
      )}
      {isKudosModalOpen && (
        <GiveRecognitionModal choosenRequest={emptyRecognitionRequest} isOpen onModalToggle={handleKudosModalClose} />
      )}
      {showNewFeatureModal && <WorkspaceIntroModal />}
      {showJoinMeetingConferenceModal && (
        <JoinMeetingConferenceModal meetingUrl={recurringMeetingConferenceUrl} onCloseModal={redirectToWorkspace} />
      )}
      {(isGoalsFreePlan || isGoalsFreeTrial || isPiDemoAccount || isPartnerAccount) && (
        <WpTooltipOverlay context={tooltip}>{ESSENTIALS_TOOLTIP}</WpTooltipOverlay>
      )}
    </WorkspaceActionCableContext.Provider>
  );
};

export default WorkspacePage;
